"use client";

import { useEffect, useState } from "react";

import { fetchAvailabilityPage } from "@/lib/cart/availability-client";
import { withRetry } from "@/lib/with-retry";
import type { DepartureDto } from "@tripcart/shared";

export interface UseAvailabilityResult {
  status: "pending" | "ready" | "error";
  departures: DepartureDto[] | null;
  windowEnd: string | null;
  hasMoreAfter: boolean;
  error?: string;
}

const PENDING: UseAvailabilityResult = {
  status: "pending",
  departures: null,
  windowEnd: null,
  hasMoreAfter: false,
};

/**
 * Client-side initial-availability fetch for the experience page. Replaces the
 * server `availabilityPromise` on the params-driven `sites` route so that
 * render path has no per-request server data dependency (prerequisite for
 * Task 5 ISR).
 *
 * Fetches the default window (no `range` → same window the SSR path fetched)
 * once per `slug`, aborting the in-flight request on slug change / unmount.
 * `status` stays `"pending"` (skeleton) until the request settles; an error
 * resolves to `departures: null` — never a false empty array — so the sidebar
 * never renders a spurious "sold out".
 *
 * A transient failure (5xx / dropped connection) is retried with exponential
 * backoff (`withRetry`) before `status` ever reaches `"error"`, so a single
 * blip on a mid-tier 4G load doesn't leave the primary booking widget
 * permanently offering no dates. The retry's per-attempt timeout is combined
 * with the caller's abort signal so a slug change / unmount still cancels
 * cleanly.
 */
export function useAvailability(
  slug: string | undefined,
): UseAvailabilityResult {
  const [result, setResult] = useState<UseAvailabilityResult>(PENDING);

  useEffect(() => {
    if (!slug) {
      setResult(PENDING);
      return;
    }
    setResult(PENDING);
    const controller = new AbortController();
    void withRetry(
      (timeoutSignal) =>
        fetchAvailabilityPage(
          slug,
          undefined,
          AbortSignal.any([timeoutSignal, controller.signal]),
        ),
      (res) => !res.ok,
    ).then((res) => {
      if (controller.signal.aborted) return;
      if (res.ok) {
        setResult({
          status: "ready",
          departures: res.page.departures,
          windowEnd: res.page.window.windowEnd,
          hasMoreAfter: res.page.window.hasMoreAfter,
        });
      } else {
        setResult({
          status: "error",
          departures: null,
          windowEnd: null,
          hasMoreAfter: false,
          error: res.error,
        });
      }
    });
    return () => controller.abort();
  }, [slug]);

  return result;
}
